import * as React from 'react';

import { AssignmentCompleteIcon, AssignmentErrorIcon, LockCircledIcon } from '@coursera/cds-icons';

import TooltipWrapper from 'bundles/common/components/Tooltip/TooltipWrapper';
import GrayCircle from 'bundles/course-frame/components/course-outline/icons/GrayCircle';
import { useIsFreemiumLocked } from 'bundles/enroll-common/utils/freemiumModelUtils';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getIsLocked, getIsLockedPGASubmissionPage } from 'bundles/learner-progress/utils/Item';

import _t from 'i18n!nls/course-frame';

type PropsFromCaller = {
  computedItem: Item;
  tooltipMessage?: string;
};

type InnerProps = Pick<PropsFromCaller, 'computedItem'>;

/**
 * Get the label for the status icon to be used for link's aria-label and a11y.
 * The list of conditions in this util should match the list of conditions in the StatusIcon component.
 * @param computedItem
 * @returns
 */
// TODO: Move this to a utils file
export const getStatusIconLabel = (computedItem: Item) => {
  if (computedItem.isFailed) {
    return _t('Failed');
  }
  if (!getIsLocked(computedItem) && (computedItem.isCompleted || computedItem.isPassed)) {
    return _t('Completed');
  }
  if (getIsLocked(computedItem)) {
    return _t('Locked');
  }
  if (!computedItem.isSubmitted) {
    return _t('Not submitted');
  }
  return '';
};

const StatusIcon = ({ computedItem }: InnerProps) => {
  const isFreemiumLocked = useIsFreemiumLocked({ moduleNumber: computedItem.weekNumber });

  if (computedItem.isFailed) {
    return <AssignmentErrorIcon color="warning" size="medium" data-testid="learn-item-warning-icon" />;
  }
  if (!getIsLocked(computedItem) && (computedItem.isCompleted || computedItem.isPassed)) {
    return <AssignmentCompleteIcon color="success" size="medium" data-testid="learn-item-success-icon" />;
  }

  if (getIsLocked(computedItem) || getIsLockedPGASubmissionPage(computedItem)) {
    if (isFreemiumLocked) {
      // We assume the entire module is locked, and would rather show all gray circles, than all locks.
      return <GrayCircle />;
    } else {
      return <LockCircledIcon color="support" size="medium" data-testid="learn-item-lock-icon" />;
    }
  }

  return <GrayCircle />;
};

// TODO(riwong): We got rid of iconLabel because it's handled up higher in Link.
// Refactor this accordingly.
const StatusIconWithTooltip = ({ computedItem, tooltipMessage }: PropsFromCaller) => {
  if (tooltipMessage) {
    return (
      <TooltipWrapper message={tooltipMessage} placement="bottom">
        <span>
          <StatusIcon computedItem={computedItem} />
        </span>
      </TooltipWrapper>
    );
  } else {
    return <StatusIcon computedItem={computedItem} />;
  }
};

export default StatusIconWithTooltip;
